/**
 * Drafting the paragraph-wise reply, and what the document cannot say about
 * itself once it is drafted.
 *
 * **The replies are the document.** They render below this as blocks and
 * download as .docx through the exporter every other document uses — no
 * template, no cause title, no verification, because the office already has
 * those in Word. What it cannot do quickly is answer forty-odd paragraphs of a
 * sixty-page application.
 *
 * So what is left here is only what a document structurally cannot report about
 * itself: a paragraph nothing answered, and a reply that is the *application*
 * rather than an answer to it. Those two are different repairs, and printing
 * them the same way is how four rounds of debugging were spent finding out
 * which had happened.
 */

import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ApiFailure, api } from '../api/client'
import type { AnswerGapView, SourceFileView } from '../api/client'
import { fileKindWords } from '../lib/court'
import { ErrorEnvelope } from './bits'
import { CourtBeforeDrafting } from './court-draft'
import { AddCourtPaper, UploadedFiles, useCasePapers } from './court-papers'
import { OfficerFacts } from './d18-workspace'
import { ANSWERS, isAnsweredParawise, paperLabel } from './pleading'

/** A refusal in the server's own words — the sentence, never a status code. */
function said(error: unknown): string {
  if (error instanceof ApiFailure) {
    return (
      error.body.error ||
      (typeof error.body.detail === 'string' ? error.body.detail : '') ||
      `The server answered ${error.status} and gave no reason. Nothing was drafted.`
    )
  }
  return error instanceof Error ? error.message : 'The request failed. Nothing was drafted.'
}

/**
 * The papers offered as the pleading to answer.
 *
 * **Only a file the office marked as the body of a pleading** — the annexures
 * and the office's own file sat beside it on the list, and an officer choosing
 * between five files for "which is the rejoinder" was being asked a question
 * the upload had already answered. A paper already chosen stays on the list
 * whatever it is, so the select never shows one thing while another is sent.
 *
 * Where no file on the case is marked as a body — a case from before the
 * question was asked at upload — every paper is offered, as it always was:
 * the officer knows which one it is and the app does not.
 *
 * The choice list does not carry the kind, so it is read off the papers list
 * by file.
 */
export function papersToAnswer<P extends { id: string }>(
  rows: P[],
  files: Pick<SourceFileView, 'id' | 'paper_kind'>[],
  chosen: string | null = null,
): { offered: P[]; bodies: P[]; narrowed: boolean } {
  const isBody = new Set(
    files.filter((file) => file.paper_kind === 'pleading-body').map((file) => file.id),
  )
  const bodies = rows.filter((paper) => isBody.has(paper.id))
  if (!bodies.length) return { offered: rows, bodies, narrowed: false }
  const offered = rows.filter((paper) => isBody.has(paper.id) || paper.id === chosen)
  return { offered, bodies, narrowed: offered.length < rows.length }
}

/** The list of papers to answer: open where one has to be picked, folded under
 *  *Change the paper* where the app already has it. Always in the page. */
function PaperChoice({ folded, children }: { folded: boolean; children: React.ReactNode }) {
  if (!folded) return <>{children}</>
  return (
    <details className="court-fold paper-choice" data-testid="replies-paper-change">
      <summary>Change the paper</summary>
      {children}
    </details>
  )
}

export function RepliesPanel({
  caseId,
  documentType,
  instruction,
  onInstruction,
  chosen,
  onChoose,
  onDraft,
  pending,
  error,
  onClearError,
}: {
  caseId: string
  documentType: string
  instruction: string
  onInstruction: (value: string) => void
  chosen: string | null
  onChoose: (id: string | null) => void
  onDraft: () => void
  pending: boolean
  /** Why the last attempt to start a draft was refused, if it was. */
  error?: unknown
  onClearError?: () => void
}) {
  const answers = ANSWERS[documentType.toUpperCase()] ?? 'the incoming pleading'
  const parawise = isAnsweredParawise(documentType)
  const papers = useQuery({
    queryKey: ['pleading-papers', caseId, documentType],
    queryFn: () => api.pleadingSourcePapers(caseId, documentType),
    retry: false,
  })
  const rows = papers.data ?? []

  // What each paper on the list *is*, as the office said at upload. The choice
  // list does not carry it, so it is read off the papers list by file.
  const files = useCasePapers(caseId)
  const kindOf = new Map(
    (files.data?.files ?? []).map((file) => [file.id, fileKindWords(file) ?? '']),
  )

  // Only the bodies of pleadings are offered, where the case has any. Where
  // exactly one fits, it is the paper to answer: chosen for the officer once,
  // in a select he can change.
  const {
    offered: choices,
    bodies,
    narrowed,
  } = papersToAnswer(rows, parawise ? (files.data?.files ?? []) : [], chosen)
  const notOffered = rows.length - choices.length
  // Which body this document answers is the server's reading: what the upload
  // said, or what the paper's first page calls itself. Chosen once, in a select
  // the officer can change. A lone body is not chosen merely for being alone —
  // that opened the sur-rejoinder with the Original Application picked as the
  // rejoinder.
  const courtCase = useQuery({
    queryKey: ['court-documents', caseId],
    queryFn: () => api.readCourtDocuments(caseId),
    enabled: parawise && bodies.length > 0,
    retry: false,
  })
  const row = courtCase.data?.documents?.find(
    (entry) => entry.document_type === documentType.toUpperCase(),
  )
  const fits = [...(row?.pleading_files ?? []), ...(courtCase.data?.unnamed_pleadings ?? [])]
  const named = bodies.filter((paper) => fits.includes(paper.filename))
  const onlyBody = courtCase.data
    ? named.length === 1
      ? named[0]?.id
      : undefined
    : courtCase.isError && bodies.length === 1
      ? bodies[0]?.id
      : undefined
  const chosenPaper = rows.find((paper) => paper.id === chosen) ?? null
  const offered = useRef(false)
  useEffect(() => {
    if (!parawise || offered.current || chosen || !onlyBody) return
    offered.current = true
    onChoose(onlyBody)
  }, [parawise, chosen, onlyBody, onChoose])

  /**
   * **Before drafting, what will be sent.** The same command as the draft with
   * `preflight` set: no model is called and nothing is written. Stage 4 on
   * Sarvam answers with the papers found, what is expected and not uploaded,
   * the size against the limit and an estimated cost; a deployment drafting the
   * earlier way answers with none of that, and this panel then reads as it
   * always did.
   *
   * It reports and never blocks: the button waits for nothing but a refusal.
   */
  const preflight = useQuery({
    queryKey: ['court-preflight', caseId, documentType, chosen],
    queryFn: () => api.previewReplies(caseId, documentType, chosen as string),
    enabled: parawise && Boolean(chosen),
    retry: false,
  })
  const court = preflight.data?.usage?.court ?? null
  // A refusal greys the button and stays beside it, in the server's words:
  // the pleading not read yet, a case too large to send, a draft already
  // running, no drafting service. The earlier path shows its error under the
  // button, as it always has.
  const courtPath = Boolean(court) || Boolean(preflight.error)
  const refusal = chosen ? (preflight.error ?? (courtPath ? error : null)) : null

  return (
    <div className="replies-draft" data-testid="replies-draft">
      <p className="muted">
        This answers {answers} paragraph by paragraph, under the pleading&apos;s own numbering.
      </p>

      {parawise && !papers.isLoading && rows.length === 0 ? (
        /* Nothing is on the case yet, so the first act is the upload and it is
           what the page opens on. */
        <>
          <AddCourtPaper caseId={caseId} documentType={documentType} />
          <UploadedFiles caseId={caseId} />
        </>
      ) : null}

      {papers.isLoading ? (
        <p className="small muted">Reading the papers on this file…</p>
      ) : rows.length === 0 ? (
        // The next act is upload, so the screen says so rather than leaving a
        // disabled button with nothing explaining it.
        <p className="small muted" data-testid="replies-no-papers">
          {parawise ? (
            <>
              No papers are on this file yet. Upload {answers} here and say that it is the Body
              of the pleading.
            </>
          ) : (
            <>
              No papers are on this file yet. Upload {answers} through <strong>Papers</strong>{' '}
              on the case first — it belongs on the file whatever is drafted from it.
            </>
          )}
        </p>
      ) : (
        <>
          {parawise && chosenPaper ? (
            /* The app has the paper to answer — the only body on the case, or
               the one its upload or its first page names as this pleading. It
               is said in a line; the list of papers is folded under it, one
               click away, for the case where it is the wrong one. */
            <p className="small" data-testid="replies-paper-chosen">
              Answering <span className="mono">{chosenPaper.filename}</span>.
            </p>
          ) : null}
          <PaperChoice folded={parawise && Boolean(chosenPaper)}>
            <label className="field">
              <span className="label">Which paper is {answers}?</span>
              <select
                aria-label={`The paper that is ${answers}`}
                value={chosen ?? ''}
                onChange={(e) => {
                  onClearError?.()
                  onChoose(e.target.value || null)
                }}
                disabled={pending}
                data-testid="replies-paper"
              >
                <option value="">Choose a paper…</option>
                {choices.map((paper) => (
                  <option key={paper.id} value={paper.id}>
                    {paperLabel(paper, kindOf.get(paper.id))}
                  </option>
                ))}
              </select>
            </label>
            {narrowed ? (
              /* Nothing is left off the list without its reason: what is not offered
               is counted, and where it is listed is said. */
              <p className="small muted" data-testid="replies-paper-narrowed">
                Only a file marked as the body of a pleading is offered. {notOffered} other{' '}
                {notOffered === 1 ? 'file is' : 'files are'} on the case, listed below with what
                each is.
              </p>
            ) : null}
          </PaperChoice>
        </>
      )}

      {parawise ? null : (
        <label className="field">
          <span className="label">
            What is the department&apos;s answer?{' '}
            <span className="small muted">Optional, in your own words.</span>
          </span>
          <textarea
            rows={3}
            value={instruction}
            onChange={(e) => onInstruction(e.target.value)}
            disabled={pending}
            placeholder="Provide confirmed departmental facts, or identify records and instructions still needed."
            data-testid="replies-instruction"
          />
        </label>
      )}

      {chosen && preflight.isLoading ? (
        <p className="small muted">Checking what this draft will be written from…</p>
      ) : null}
      {court ? <CourtBeforeDrafting caseId={caseId} court={court} /> : null}

      <div
        className={refusal ? 'blocked court-start' : 'court-start'}
        data-testid="replies-start"
      >
        <button
          type="button"
          className="btn"
          onClick={onDraft}
          disabled={pending || !chosen || Boolean(refusal)}
          data-testid="replies-generate"
        >
          {parawise
            ? pending
              ? 'Generating replies…'
              : 'Generate replies'
            : pending
              ? 'Reading the application…'
              : 'Draft the replies'}
        </button>
        {refusal ? (
          <div>
            <div className="why" role="alert" data-testid="replies-refused">
              {said(refusal)}
            </div>
            <button
              type="button"
              className="quiet"
              onClick={() => {
                onClearError?.()
                void preflight.refetch()
              }}
            >
              Check again
            </button>
          </div>
        ) : null}
      </div>
      {!courtPath ? <ErrorEnvelope error={error} /> : null}
      {parawise ? (
        <p className="small muted">
          One reply is drafted for every paragraph of {answers}. Missing information never stops
          drafting; short questions appear afterwards.
        </p>
      ) : null}
      {parawise ? (
        /* What the draft is started from comes first: the paper, what it will
           be written from, the button. The two things an officer does only
           sometimes — add a paper, add a fact — are folded below, one click
           away, and open by themselves where there is nothing on the case. */
        <>
          {rows.length > 0 ? (
            <details className="court-fold" data-testid="replies-add-paper">
              <summary>
                Add or replace a paper{' '}
                <span className="small muted">
                  {(files.data?.files ?? []).length} on this case
                </span>
              </summary>
              <AddCourtPaper caseId={caseId} documentType={documentType} />
              <UploadedFiles caseId={caseId} />
            </details>
          ) : null}
          <details className="court-fold" data-testid="replies-facts">
            <summary>
              Facts you confirm <span className="small muted">optional</span>
            </summary>
            {/* D18 Replies Lite: a known departmental fact is entered once, here
                or in Case materials. No free-form instruction box and no
                background reading run start when the OA is chosen. */}
            <OfficerFacts caseId={caseId} sourceId={chosen} />
          </details>
        </>
      ) : null}
      {pending ? (
        <p className="small muted" style={{ marginTop: 8 }}>
          {court
            ? 'Starting the draft. The replies are then written in the background and appear on the page as they are finished.'
            : parawise
              ? 'Drafting the replies. This usually takes a few minutes.'
              : 'Replies are being prepared against the selected source. Missing replies will remain visible.'}
        </p>
      ) : null}
    </div>
  )
}

export function Replies({
  numbered,
  answered,
  labels,
  missing,
  copied,
  failures,
}: {
  numbered: string
  answered: number
  labels: string[]
  missing: string[]
  copied: AnswerGapView[]
  failures: string[]
}) {
  const [copiedNow, setCopiedNow] = useState(false)

  const copy = () => {
    void navigator.clipboard.writeText(numbered).then(() => {
      setCopiedNow(true)
      window.setTimeout(() => setCopiedNow(false), 2000)
    })
  }

  const complete = labels.length > 0 && missing.length === 0 && failures.length === 0

  return (
    <div
      className={complete && !copied.length ? 'replies settled' : 'replies'}
      data-testid="replies"
    >
      <div className="row between">
        <p className="strong">
          {labels.length
            ? `${answered} of ${labels.length} paragraphs answered.`
            : `${answered} replies.`}
        </p>
        {/* The document downloads; this is for the office's own Word file,
            which is where the cause title and the verification live. */}
        <button type="button" className="quiet" onClick={copy} data-testid="replies-copy">
          {copiedNow ? 'Copied' : 'Copy the replies'}
        </button>
      </div>

      {/* **Not a row in a list.** A reply that is the application answers
          nothing, and the draft it appears in reads as the best one yet. */}
      {copied.length ? (
        <p className="lifted" data-testid="replies-copied">
          <strong>
            {copied.length === 1
              ? 'One reply is the application’s own words, not an answer to it.'
              : `${copied.length} replies are the application’s own words, not answers to them.`}
          </strong>{' '}
          Read {copied.length === 1 ? 'it' : 'them'} before anything is signed — filed, it would
          be the department adopting the applicant’s case on oath.
          <span className="small"> {copied.map((c) => c.paragraph).join(', ')}</span>
        </p>
      ) : null}

      {missing.length ? (
        <p className="small warn" data-testid="replies-missing">
          <strong>Nothing answered {missing.join(', ')}.</strong> Rule 12(2) requires every
          averment to be admitted, denied or explained; an unanswered one stands unrebutted.
          Draft again to fill {missing.length === 1 ? 'it' : 'them'}.
        </p>
      ) : null}

      {failures.map((f, n) => (
        <p className="small warn" key={n}>
          Drafting needs attention — {f}
        </p>
      ))}

      {complete && !copied.length ? (
        <p className="small muted">
          Every paragraph the application carries, including its sub-paragraphs. Read them
          against the application before they go to counsel.
        </p>
      ) : null}
    </div>
  )
}
